/**
 * VELLUM cart page: set upgrade, journal offer, pen recover, linked journal cleanup.
 */
(function () {
  'use strict';

  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  async function fetchCart() {
    if (window.VellumCart && window.VellumCart.fetchCart) return window.VellumCart.fetchCart();
    const res = await fetch(`${ROOT()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Sepet yüklenemedi');
    return res.json();
  }

  async function changeItem(payload) {
    if (window.VellumCart && window.VellumCart.changeItem) return window.VellumCart.changeItem(payload);
    const res = await fetch(`${ROOT()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Güncellenemedi');
    return data;
  }

  async function addItems(items) {
    if (window.VellumCart && window.VellumCart.addItems) return window.VellumCart.addItems(items);
    const payload = Array.isArray(items) ? { items } : { items: [items] };
    const res = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Eklenemedi');
    return data;
  }

  async function updateCart(updates) {
    if (window.VellumCart && window.VellumCart.updateCart) return window.VellumCart.updateCart(updates);
    const res = await fetch(`${ROOT()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || 'Güncellenemedi');
    return data;
  }

  async function removeOrphanJournals(keys) {
    if (!keys || !keys.length) return false;
    const updates = {};
    keys.forEach((key) => {
      if (key) updates[key] = 0;
    });
    await updateCart({ updates });
    return true;
  }

  function isJournalItem(item) {
    const handle = item.handle || '';
    return (
      handle === 'vellum-vintage-yaprak-desenli-deri-defter' ||
      handle.includes('deri-defter') ||
      !!(item.properties && item.properties._vellum_upsell)
    );
  }

  function getPenQty(cart, penProductId) {
    const penId = Number(penProductId || 0);
    return (cart.items || []).reduce((sum, item) => {
      if (penId && Number(item.product_id) === penId) return sum + Number(item.quantity || 0);
      const handle = item.handle || '';
      if (handle.includes('divit-kalem')) return sum + Number(item.quantity || 0);
      return sum;
    }, 0);
  }

  function cartSignature(cart) {
    return (cart.items || [])
      .map((item) => `${item.product_id}:${item.quantity}:${item.key}`)
      .sort()
      .join('|');
  }

  async function syncLinkedJournalFromCart(cart, penProductId) {
    const penId = Number(penProductId || 0);
    const penQty = getPenQty(cart, penId);
    const hasPen = penQty > 0;

    const toRemove = (cart.items || []).filter((item) => isJournalItem(item) && !hasPen);
    if (toRemove.length) {
      const updates = {};
      toRemove.forEach((item) => {
        updates[item.key] = 0;
      });
      await updateCart({ updates });
      return true;
    }

    const journals = (cart.items || []).filter(isJournalItem);
    if (!journals.length || !hasPen) return false;

    const journalQty = journals.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (journalQty === penQty) return false;

    const updates = {};
    journals.forEach((item, idx) => {
      updates[item.key] = idx === 0 ? penQty : 0;
    });
    await updateCart({ updates });
    return true;
  }

  async function fixStaleJournalPrice(cart, offersRoot) {
    const journalVariantId = Number((offersRoot && offersRoot.dataset.journalVariantId) || 0);
    const offerCents = Number((offersRoot && offersRoot.dataset.journalOfferCents) || 29900);
    const penProductId = (offersRoot && offersRoot.dataset.penProductId) || '';
    if (!journalVariantId) return false;

    const stale = (cart.items || []).filter((item) => {
      const props = item.properties || {};
      if (!props._vellum_upsell) return false;
      const unit = Math.round(Number(item.final_price || item.price || 0));
      return unit > offerCents;
    });

    if (!stale.length) return false;

    const updates = {};
    stale.forEach((item) => {
      updates[item.key] = 0;
    });
    await updateCart({ updates });

    const penQty = getPenQty(cart, penProductId);
    if (penQty < 1) return true;

    await addItems({
      id: journalVariantId,
      quantity: penQty,
      properties: {
        _vellum_upsell: 'cart-price-fix',
        _vellum_parent_id: String(penProductId),
        _vellum_offer_cents: String(offerCents),
        _vellum_match_sets: '1',
      },
    });
    return true;
  }

  function bindOffers(root) {
    if (!root || root.dataset.vellumBound === '1') return;
    root.dataset.vellumBound = '1';

    const penKey = root.dataset.penLineKey;
    const penProductId = root.dataset.penProductId;
    const penVariantId = Number(root.dataset.penVariantId || 0);
    const journalVariantId = Number(root.dataset.journalVariantId || 0);
    const journalOfferCents = Number(root.dataset.journalOfferCents || 29900);
    const journalCode = (root.dataset.journalDiscountCode || '').trim();
    const code2 = (root.dataset.discountCode2 || '').trim();
    const code3 = (root.dataset.discountCode3 || '').trim();

    root.querySelectorAll('[data-vellum-set-qty]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const qty = Number(btn.dataset.vellumSetQty || 0);
        if (!penKey || !qty) return;
        btn.disabled = true;
        try {
          await changeItem({ id: penKey, quantity: qty });
          const code = qty === 3 ? code3 : qty === 2 ? code2 : '';
          if (code) {
            try {
              await updateCart({ discount: code });
            } catch (e) {
              console.warn('[VELLUM] set discount code', e);
            }
          }
          try {
            const cart = await fetchCart();
            await syncLinkedJournalFromCart(cart, penProductId);
          } catch (e) {
            console.warn('[VELLUM] journal sync after set upgrade', e);
          }
          window.location.reload();
        } catch (err) {
          console.error(err);
          alert(err.message || 'Sepet güncellenemedi');
          btn.disabled = false;
        }
      });
    });

    root.querySelectorAll('[data-vellum-add-pen-qty]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const qty = Number(btn.dataset.vellumAddPenQty || 0);
        if (!penVariantId || !qty) return;
        btn.disabled = true;
        try {
          const discount = qty === 3 ? 25 : qty === 2 ? 15 : 0;
          await addItems({
            id: penVariantId,
            quantity: qty,
            properties: {
              _vellum_paket: qty === 1 ? '1 Set' : `${qty} Set`,
              _vellum_qty: String(qty),
              _vellum_discount_pct: String(discount),
            },
          });
          const code = qty === 3 ? code3 : qty === 2 ? code2 : '';
          if (code) {
            try {
              await updateCart({ discount: code });
            } catch (e) {
              console.warn('[VELLUM] pen recover discount', e);
            }
          }
          window.location.reload();
        } catch (err) {
          console.error(err);
          alert(err.message || 'Set eklenemedi');
          btn.disabled = false;
        }
      });
    });

    const journalBtn = root.querySelector('[data-vellum-add-journal]');
    if (journalBtn) {
      journalBtn.addEventListener('click', async () => {
        if (!journalVariantId) return;
        journalBtn.disabled = true;
        try {
          const penQty = Math.max(1, Number(root.dataset.penQty || 1));
          const journalQty = Math.max(0, Number(root.dataset.journalQty || 0));
          const need = Math.max(
            1,
            Number(journalBtn.dataset.journalQty || root.dataset.journalNeed || penQty - journalQty || penQty)
          );
          const journalLineKey = (root.dataset.journalLineKey || '').trim();

          if (journalLineKey && journalQty > 0) {
            await changeItem({ id: journalLineKey, quantity: penQty });
          } else {
            await addItems({
              id: journalVariantId,
              quantity: need,
              properties: {
                _vellum_upsell: 'cart-page',
                _vellum_parent_id: String(penProductId || ''),
                _vellum_offer_cents: String(journalOfferCents),
                _vellum_match_sets: '1',
              },
            });
          }
          if (journalCode) {
            try {
              await updateCart({ discount: journalCode });
            } catch (e) {
              console.warn('[VELLUM] journal discount code', e);
            }
          }
          window.location.reload();
        } catch (err) {
          console.error(err);
          alert(err.message || 'Defter eklenemedi');
          journalBtn.disabled = false;
        }
      });
    }
  }

  function getPenProductIdFromDom() {
    const offers = document.querySelector('[data-vellum-cart-offers]');
    if (offers && offers.dataset.penProductId) return offers.dataset.penProductId;
    const host = document.querySelector('[data-vellum-cart-offers-host]');
    return (host && host.dataset.penProductId) || '';
  }

  let reloadScheduled = false;
  function scheduleCartPageReload() {
    if (reloadScheduled) return;
    reloadScheduled = true;
    setTimeout(() => {
      window.location.reload();
    }, 80);
  }

  async function onCartUpdated() {
    try {
      const cart = await fetchCart();
      const penId = getPenProductIdFromDom();
      await syncLinkedJournalFromCart(cart, penId);

      const nextSig = cartSignature(await fetchCart());
      const prevSig = (window.VellumCartPage && window.VellumCartPage.lastCartSig) || '';
      window.VellumCartPage = window.VellumCartPage || {};
      window.VellumCartPage.lastCartSig = nextSig;

      // Always reload so Liquid re-renders journal / pen recover offers
      if (nextSig !== prevSig || !document.querySelector('[data-vellum-cart-offers]')) {
        scheduleCartPageReload();
        return;
      }
    } catch (e) {
      scheduleCartPageReload();
    }
  }

  function bindCartUpdateListeners() {
    if (window.VellumCartPage && window.VellumCartPage.listenersBound) return;
    window.VellumCartPage = window.VellumCartPage || {};
    window.VellumCartPage.listenersBound = true;

    document.addEventListener('cart:updated', onCartUpdated);
    document.addEventListener('vellum:cart:updated', onCartUpdated);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdated);
    }

    // Backup: native cart remove / qty controls (AJAX may finish before pubsub)
    document.addEventListener(
      'click',
      (e) => {
        const removeBtn = e.target.closest('cart-remove-button, .cart-remove-button, [name="minus"]');
        if (!removeBtn) return;
        // After theme finishes AJAX, force reload so offers return
        setTimeout(() => {
          onCartUpdated();
        }, 450);
      },
      true
    );

    document.addEventListener(
      'change',
      (e) => {
        if (!e.target.closest('#main-cart-items, cart-items')) return;
        if (!e.target.matches('.quantity__input, [name="updates[]"]')) return;
        setTimeout(() => {
          onCartUpdated();
        }, 450);
      },
      true
    );
  }

  async function boot() {
    const presetKeys = (window.VellumCartPage && window.VellumCartPage.orphanJournalKeys) || [];
    if (presetKeys.length) {
      try {
        await removeOrphanJournals(presetKeys);
        window.location.reload();
        return;
      } catch (e) {
        console.error(e);
      }
    }

    bindCartUpdateListeners();

    try {
      const cart = await fetchCart();
      window.VellumCartPage = window.VellumCartPage || {};
      window.VellumCartPage.lastCartSig = cartSignature(cart);
    } catch (e) {
      /* ignore */
    }

    const offers = document.querySelector('[data-vellum-cart-offers]');
    if (offers) {
      bindOffers(offers);
      try {
        const cart = await fetchCart();
        if (await fixStaleJournalPrice(cart, offers)) {
          window.location.reload();
          return;
        }
        const changed = await syncLinkedJournalFromCart(cart, offers.dataset.penProductId);
        if (changed) {
          window.location.reload();
          return;
        }
      } catch (e) {
        console.warn(e);
      }
    } else {
      try {
        const cart = await fetchCart();
        const penId = Number(getPenProductIdFromDom() || 0);
        const hasPen = getPenQty(cart, penId) > 0;
        const toRemove = (cart.items || []).filter((item) => isJournalItem(item) && !hasPen);
        if (toRemove.length) {
          const updates = {};
          toRemove.forEach((item) => {
            updates[item.key] = 0;
          });
          await updateCart({ updates });
          window.location.reload();
        }
      } catch (e) {
        /* ignore */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
